/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import clsx from "clsx";
import BookingLoginReservationDetailInputBlank
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationDetailInputBlank";
import Dates from "utils/Dates";
import {padMinutes} from "utils/utility";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {Reservation} from "types/entities/Reservation";
import {useTranslation} from "react-i18next";
import Loader from "components/Loader";
import {PencilIcon} from "@heroicons/react/24/outline";
import {closeModal, openModal} from "helpers/actions";
import {ModalSize} from "pages/Front/components/Popup";
import {useDispatch} from "useDispatch";
import BookingSellerCompanyModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerCompanyModal";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import {toast} from "react-toastify";

interface BookingSellerReservationInfoCardProps {
    reservation: Reservation;
    className?: string;
}

const BookingSellerReservationInfoCard = (props: BookingSellerReservationInfoCardProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const company = props.reservation.company;

    const onClickEdit = () => {
        console.log("reservation", props.reservation);
        dispatch(
            openModal(<BookingSellerCompanyModal reservation={props.reservation} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    toast.success("Closing now");
                    dispatch(BookingLoginAction.fetchSellerReservationDetail(props.reservation.id)).then(r => {
                        dispatch(closeModal());
                    });
                },
            }),
        );
    };

    if (!props.reservation) {
        return <Loader />;
    }

    return (
        <BookingCard className={clsx("BookingSellerReservationInfoCard !px-0 !py-0 mb-6", props.className)}>
            {/*<BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={t("Reservation basic information")} />*/}
            <h2 className={clsx("BookingCardTitle font-bold text-xl text-black px-3 py-2.5 xl:py-2.5  border-b flex items-center justify-between")}>
                <div className={"inline-flex items-center gap-x-2"}>
                    <span>{t("Reservation basic information")}</span>
                </div>
                <button
                    className={"bg-blue-400 hover:bg-blue-500 px-2 py-1 text-white rounded text-base flex flex-row items-center"}
                    onClick={onClickEdit}>
                    <PencilIcon className={"w-4 h-4 mr-1.5"} />
                    <span>{t("Edit company")}</span>
                </button>
            </h2>
            <div className={"grid sm:grid-cols-2 md:grid-cols-4 gap-3 px-3 py-3"}>
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    classNameInput={" !bg-red-400/50"}
                    label={t("Seller Name")}
                    name={"seller_name"}
                    value={props.reservation?.seller ? props.reservation.seller.name + " " + props.reservation.seller.last_name : "---"}
                />
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("Check-in Code")}
                    name={"code"}
                    value={props.reservation?.guid ?? "---"}
                />
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("Package Cart ID")}
                    name={"package cart"}
                    value={props.reservation && props.reservation.packagesCart && "guid" in props.reservation.packagesCart ? props.reservation.packagesCart.guid : "---"}
                />
                <div className={"col-span-1"}>
                    <br />
                </div>
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("Reservation at")}
                    name={"reservation_at"}
                    value={props.reservation?.date ? Dates.ymd(props.reservation?.date, "DD.MM.YYYY") : "---"}
                />
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("Start")}
                    name={"hour"}
                    value={props.reservation?.hour ? padMinutes(props.reservation.hour) + ":" + padMinutes(props.reservation.minute) : "---"}
                />
                <BookingLoginReservationDetailInputBlank className={"col-span-1"} label={t("Persons")} name={"personen"}
                                                         value={props.reservation.persons} />
                <BookingLoginReservationDetailInputBlank className={"col-span-1"} label={t("Company name")}
                                                         name={"name"} value={company?.name ?? "---"} />
                {company?.email && (
                    <BookingLoginReservationDetailInputBlank className={"col-span-1"} label={t("Email")} name={"email"}
                                                             value={company?.email ?? "---"} />
                )}
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("First name")}
                    name={"first_name"}
                    value={company?.first_name ?? "---"}
                />
                <BookingLoginReservationDetailInputBlank
                    className={"col-span-1"}
                    label={t("Last name")}
                    name={"last_name"}
                    value={company?.last_name ?? "---"}
                />
                {company?.phone && (
                    <BookingLoginReservationDetailInputBlank className={"col-span-1"} label={t("Phone")} name={"phone"}
                                                             value={company?.phone ?? "---"} />
                )}
            </div>
        </BookingCard>
    );
};

export default BookingSellerReservationInfoCard;
